import { twMerge } from "tailwind-merge";

export enum TYPO_LAYOUT {
    base = "base",
    bodyExtraBold14 = "bodyExtraBold14",
    bodyExtraBold16 = "bodyExtraBold16",
    bodyExtraBold18 = "bodyExtraBold18",
    bodyItalic14 = "bodyItalic14",
    bodyItalic16 = "bodyItalic16",
    bodyItalic18 = "bodyItalic18",
    bodyNormal14 = "bodyNormal14",
    bodyNormal16 = "bodyNormal16",
    bodyNormal18 = "bodyNormal18",
    bodyUnderline18 = "bodyUnderline18",
    error = "error",
    headlineBlack1 = "headlineBlack1",
    headlineBlack2 = "headlineBlack2",
    headlineBlackUnderline1 = "headlineBlackUnderline1",
    headlineBlackUnderline2 = "headlineBlackUnderline2",
    headlineExtraBold3 = "headlineExtraBold3",
    headlineExtraBold4 = "headlineExtraBold4",
    headlineExtraBold4Rich = "headlineExtraBold4Rich",
    headlineExtraBold5 = "headlineExtraBold5",
    headlinePlain1 = "headlinePlain1",
    headlinePlain2 = "headlinePlain2",
    headlinePlain3 = "headlinePlain3",
    headlinePlain4 = "headlinePlain4",
    headlinePlain5 = "headlinePlain5",
    headlineUnderline3 = "headlineUnderline3",
    headlineUnderline4 = "headlineUnderline4",
    icon = "icon",
    richtext = "richtext",
}

export const TYPO_STYLES: Record<TYPO_LAYOUT, () => string> = {
    base: () => "font-sans",
    headlineBlack1: () => twMerge(TYPO_STYLES.base(), "text-5xl lg:text-6xl font-black"),
    headlineBlackUnderline1: () => twMerge(TYPO_STYLES.headlineBlack1(), "underline-offset-2"),
    headlinePlain1: () => twMerge(TYPO_STYLES.headlineBlack1(), "font-normal"),
    headlineBlack2: () => twMerge(TYPO_STYLES.base(), "text-4xl lg:text-5xl font-black"),
    headlineBlackUnderline2: () => twMerge(TYPO_STYLES.headlineBlack2(), "underline-offset-2"),
    headlinePlain2: () => twMerge(TYPO_STYLES.base(), "text-4xl lg:text-5xl font-normal"),
    headlineExtraBold3: () => twMerge(TYPO_STYLES.base(), "text-3xl lg:text-4xl font-extrabold"),
    headlinePlain3: () => twMerge(TYPO_STYLES.base(), "text-3xl lg:text-4xl font-normal"),
    headlineUnderline3: () =>
        twMerge(TYPO_STYLES.base(), "text-3xl lg:text-4xl font-extrabold underline-offset-2"),
    headlineExtraBold4: () => twMerge(TYPO_STYLES.base(), "text-2xl lg:text-3xl font-extrabold"),
    headlineExtraBold4Rich: () =>
        twMerge(
            TYPO_STYLES.base(),
            "[&_h2]:text-2xl lg:[&_h2]:text-3xl [&_h2]:font-extrabold [&_h3]:text-2xl lg:[&_h3]:text-3xl [&_h3]:font-extrabold [&_h4]:text-2xl lg:[&_h4]:text-3xl [&_h4]:font-extrabold [&_h5]:text-2xl lg:[&_h5]:text-3xl [&_h5]:font-extrabold",
        ),
    headlinePlain4: () => twMerge(TYPO_STYLES.base(), "text-2xl lg:text-3xl font-normal"),
    headlineUnderline4: () =>
        twMerge(TYPO_STYLES.base(), "text-2xl lg:text-3xl font-extrabold underline-offset-2"),
    headlineExtraBold5: () => twMerge(TYPO_STYLES.base(), "text-xl lg:text-2xl font-extrabold"),
    headlinePlain5: () => twMerge(TYPO_STYLES.base(), "text-xl lg:text-2xl font-normal"),
    icon: () => "font-icons text-inherit",
    bodyNormal18: () => twMerge(TYPO_STYLES.base(), "text-lg font-light"),
    bodyItalic18: () => twMerge(TYPO_STYLES.base(), "text-lg font-light italic"),
    bodyUnderline18: () => twMerge(TYPO_STYLES.base(), "text-lg font-light  underline-offset-2"),
    bodyExtraBold18: () => twMerge(TYPO_STYLES.base(), "text-lg font-extrabold"),

    bodyNormal16: () => twMerge(TYPO_STYLES.base(), "text-base font-light"),
    bodyItalic16: () => twMerge(TYPO_STYLES.base(), "text-base font-light italic"),
    bodyExtraBold16: () => twMerge(TYPO_STYLES.base(), "text-base font-extrabold"),

    bodyNormal14: () => twMerge(TYPO_STYLES.base(), "text-sm font-light"),
    bodyItalic14: () => twMerge(TYPO_STYLES.base(), "text-sm font-light italic"),
    bodyExtraBold14: () => twMerge(TYPO_STYLES.base(), "text-sm font-extrabold"),

    richtext: () =>
        twMerge("[&_p]:font-light [&_p]:text-lg [&_a]:underline [&_a]:underline-offset-4"),
    error: () => twMerge(TYPO_STYLES.base(), "text-base text-redError text-center"),
};

export enum ICONS {
    IconAdCircle = 0xf79e,
}

export const toCSSCode = (code: ICONS | undefined): string => {
    return (code && "\\" + code.toString(16)) ?? "";
};

export const toHTMLCode = (code: ICONS | undefined): string => {
    return (code && String.fromCharCode(code)) ?? "";
};

export enum INPUT_LAYOUT {
    base = "base",
    dropdown = "dropdown",
    input = "input",
    textarea = "textarea",
}

export const INPUT_STYLES: Record<INPUT_LAYOUT, () => string> = {
    base: () =>
        "border py-2 px-4 bg-greyOpacity05 border-brandDark text-brandDark hover:bg-greyOpacity10 outline-none disabled:border-grey6 disabled:text-grey6 disabled:cursor-not-allowed disabled:hover:bg-greyOpacity05",
    input: () =>
        twMerge(
            INPUT_STYLES.base(),
            "rounded-t border-t-0 border-l-0 border-r-0 relative input-border-bottom",
        ),
    dropdown: () =>
        twMerge(
            INPUT_STYLES.base(),
            "rounded-t border-t-0 border-l-0 border-r-0 border-b hover:cursor-pointer input-border-bottom",
        ),
    textarea: () => twMerge(INPUT_STYLES.base(), "rounded textarea-border-bottom"),
};
